import React, { useEffect, useState } from "react";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import ManagerApprovalDialog from "./ManagerApprovalDialog";
import GiftCardActions from "./GiftCardActions";

/** Payout methods accepted by both full-order and line-item refund flows. */
export const REFUND_PAYOUT_TYPES = [
  { value: "points",         label: "Loyalty points" },
  { value: "cash",           label: "Cash" },
  { value: "credit_card",    label: "Credit Card" },
  { value: "online",         label: "Online" },
  { value: "gift_card",      label: "Gift Card" },
  { value: "bank_transfer",  label: "Bank Transfer" },
];

/**
 * Whole-order refund dialog. Handles partial-amount, payout method
 * selection, gift-card issuance, and the Edit-only "request approval"
 * hand-off to the Manager Approval popup.
 */
export default function RefundDialog({ open, onOpenChange, order, branch, onRefunded }) {
  const { staff } = useAuth();
  const canAuthorise = hasPermission(staff, "location.orders_pos.delete");
  const alreadyRefunded = (order?.refunds || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const refundable = Math.max(0, (order?.amount_paid || 0) - alreadyRefunded);
  const [amount, setAmount] = useState(String(refundable));
  const [payoutType, setPayoutType] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Gift-Card refund flow: when payout is `gift_card` we prompt for a
  // validity date via a follow-up dialog before hitting the endpoint.
  const [askGiftExpiry, setAskGiftExpiry] = useState(false);
  const [giftExpiry, setGiftExpiry] = useState("");
  // Issued-card confirmation: filled after a successful gift-card refund
  // so the operator can print / email the new voucher right away.
  const [issuedCard, setIssuedCard] = useState(null);
  // Payload buffered when an Edit-only operator submits — hands off to
  // the Manager Approval popup.
  const [approvalPayload, setApprovalPayload] = useState(null);

  useEffect(() => {
    if (open) {
      setAmount(String(refundable));
      setPayoutType("cash");
      setNote("");
      setAskGiftExpiry(false);
      // Default expiry: 12 months from today, matches the backend fallback.
      const d = new Date();
      d.setMonth(d.getMonth() + 12);
      setGiftExpiry(d.toISOString().slice(0, 10));
    }
  }, [open, refundable]);

  const submitRefund = async (giftCardExpiresAt) => {
    const n = parseFloat(amount);
    if (!(n > 0)) { toast.error("Refund amount must be > 0"); return; }
    if (n > refundable + 0.01) { toast.error("Amount exceeds refundable balance"); return; }
    // Mandatory reason — one-time-only refund policy requires an
    // auditable "why" on every event. Trim so whitespace-only fails.
    const reason = (note || "").trim();
    if (!reason) { toast.error("A reason for the refund is required."); return; }
    const body = { amount: n, payout_type: payoutType, note: reason };
    if (payoutType === "gift_card" && giftCardExpiresAt) body.gift_card_expires_at = giftCardExpiresAt;
    // Edit-only user → hand off to Manager Approval popup instead of
    // hitting `/refund` directly (backend would return 403).
    if (!canAuthorise) {
      setApprovalPayload(body);
      setAskGiftExpiry(false);
      return;
    }
    setBusy(true);
    try {
      const r = await staffApi.post(`/orders/${order.id}/refund`, body);
      if (r.data?.gift_card?.code) {
        setIssuedCard(r.data.gift_card);
      } else if (r.data?.credited_points > 0) {
        toast.success(`${r.data.credited_points.toLocaleString()} points credited to the customer`);
      }
      onRefunded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setAskGiftExpiry(false); }
  };

  const onConfirm = () => {
    // Mirror the submit-time reason check so the gift-card branch also
    // catches an empty reason before opening the expiry sub-dialog.
    if (!(note || "").trim()) {
      toast.error("A reason for the refund is required.");
      return;
    }
    if (payoutType === "gift_card") {
      setAskGiftExpiry(true);
      return;
    }
    submitRefund();
  };
  const reasonValid = (note || "").trim().length > 0;

  return (
    <>
      <Dialog open={open && !approvalPayload} onOpenChange={onOpenChange}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund this Order</DialogTitle>
            <DialogDescription className="sr-only">Refund all or part of this order to the chosen payout method.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="text-sm">Refundable balance: <span className="font-medium">{formatBranchMoney(branch, refundable)}</span></div>
            <div>
              <Label>Amount to refund</Label>
              <Input type="number" min={0} step={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input" />
            </div>
            <div>
              <Label htmlFor="refund-payout-select">Refund to</Label>
              <div className="relative mt-1">
                <select
                  id="refund-payout-select"
                  value={payoutType}
                  onChange={(e) => setPayoutType(e.target.value)}
                  className="h-10 w-full cursor-pointer appearance-none rounded-md border border-input bg-background px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="refund-payout-select"
                >
                  {REFUND_PAYOUT_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
              </div>
            </div>
            <div>
              <Label htmlFor="refund-note-input">
                Reason for refund <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="refund-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Required — e.g. Client dissatisfied, duplicate charge"
                data-testid="refund-note-input"
                aria-required="true"
                aria-invalid={!reasonValid}
              />
              {!reasonValid && (
                <p className="text-[11px] text-rose-600 mt-1" data-testid="refund-note-required">
                  A reason is required — one-time-only refund, logged in the audit trail.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="refund-cancel-btn">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={onConfirm} disabled={busy || !reasonValid} data-testid="refund-confirm-btn">
              {busy ? "Refunding…" : (canAuthorise ? "Refund" : "Request refund…")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManagerApprovalDialog
        open={!!approvalPayload}
        onOpenChange={(o) => { if (!o) setApprovalPayload(null); }}
        orderId={order?.id}
        kind="refund"
        payload={approvalPayload || {}}
        actionLabel="Refund order"
        onApproved={() => { setApprovalPayload(null); onRefunded(); }}
        onQueued={() => { setApprovalPayload(null); onOpenChange(false); }}
      />

      <Dialog open={askGiftExpiry} onOpenChange={setAskGiftExpiry}>
        <DialogContent data-testid="refund-gift-expiry-dialog" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Gift Card Expiry</DialogTitle>
            <DialogDescription className="sr-only">Choose the validity date for the gift card issued as this refund.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="gift-expiry">Valid until</Label>
            <Input
              id="gift-expiry"
              type="date"
              value={giftExpiry}
              onChange={(e) => setGiftExpiry(e.target.value)}
              data-testid="refund-gift-expiry-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAskGiftExpiry(false)} data-testid="refund-gift-expiry-cancel">Back</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => submitRefund(giftExpiry)}
              disabled={busy || !giftExpiry}
              data-testid="refund-gift-expiry-confirm"
            >
              {busy ? "Issuing…" : "Issue Gift Card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issuedCard} onOpenChange={(v) => { if (!v) setIssuedCard(null); }}>
        <DialogContent data-testid="refund-gift-issued-dialog" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Gift Card Issued</DialogTitle>
            <DialogDescription className="sr-only">The refund was issued as a new gift card.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-center font-mono text-base tracking-wider" data-testid="refund-gift-issued-code">{issuedCard?.code}</div>
            <div className="text-center text-xs text-muted-foreground">
              Valid until {issuedCard?.expires_at ? String(issuedCard.expires_at).slice(0, 10) : "—"} · Balance {formatBranchMoney(branch, issuedCard?.balance || 0)}
            </div>
            <div className="flex justify-center [&_span]:text-xs">
              {issuedCard?.code && <GiftCardActions code={issuedCard.code} />}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIssuedCard(null)} data-testid="refund-gift-issued-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
